import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.tsx'
import { api } from '../api.ts'
import { unitStatusLabel, ageOnLot, categoryLabel, CATEGORIES } from '../format.ts'
import { IntakeDialog } from '../components/IntakeDialog.tsx'

interface UnitRow {
  id: string
  serialNumber: string | null
  productionYear: number | null
  status: string
  receivedAt: string | null
  model: string
  catalogCode: string
  category: string
  manufacturerName: string
  isReserved: boolean
  reservationExpired: boolean
  isSold: boolean
  isAvailable: boolean
  agedOnYard: boolean
  reservationConflict: boolean
}

/** The lot: every physical unit, its status and how long it's been standing here. */
export function WarehousePage() {
  const { user } = useAuth()
  // Intake is a sales/owner action (grants 0014); service only looks.
  const mozePrzyjmowac = user?.role === 'wlasciciel' || user?.role === 'handlowiec'
  const [units, setUnits] = useState<UnitRow[]>([])
  const [status, setStatus] = useState('')
  const [category, setCategory] = useState('')
  const [aged, setAged] = useState(false)
  const [search, setSearch] = useState('')
  const [intake, setIntake] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function reload() {
    const p = new URLSearchParams()
    if (status) p.set('status', status)
    if (category) p.set('category', category)
    if (aged) p.set('aged', 'true')
    if (search.trim()) p.set('search', search.trim())
    api
      .get<{ units: UnitRow[] }>(`/api/units?${p.toString()}`)
      .then((r) => setUnits(r.units))
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(() => {
    const t = setTimeout(reload, 120)
    return () => clearTimeout(t)
  }, [status, category, aged, search])

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Magazyn egzemplarzy</h1>
          <p className="text-sm text-slate-500">Fizyczne maszyny na placu — numery seryjne, rezerwacje, wydania.</p>
        </div>
        {mozePrzyjmowac && (
          <button
            onClick={() => setIntake(true)}
            className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 font-medium shrink-0"
          >
            Przyjmij egzemplarz
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Numer seryjny, model…"
          className="flex-1 min-w-56 px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:border-brand-600"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
          <option value="">Każdy status</option>
          <option value="on_order">Zamówiony</option>
          <option value="in_transit">W transporcie</option>
          <option value="in_stock">Na placu</option>
          <option value="handed_over">Wydany</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
          <option value="">Każda kategoria</option>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600 px-2">
          <input type="checkbox" checked={aged} onChange={(e) => setAged(e.target.checked)} />
          Zalega &gt;180 dni
        </label>
      </div>

      {error && <div className="text-sm text-red-700 mb-3">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-400">
            <tr>
              <th className="text-left font-medium px-4 py-2">Numer seryjny</th>
              <th className="text-left font-medium px-4 py-2">Maszyna</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
              <th className="text-left font-medium px-4 py-2">Dostępność</th>
              <th className="text-left font-medium px-4 py-2">Na placu</th>
            </tr>
          </thead>
          <tbody>
            {units.map((u) => {
              const age = ageOnLot(u.receivedAt)
              return (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/magazyn/${u.id}`} className="text-brand-700 font-medium hover:underline font-mono">
                      {u.serialNumber ?? '— bez numeru —'}
                    </Link>
                    {u.productionYear && <span className="text-xs text-slate-400 ml-2">rok {u.productionYear}</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {u.model}
                    <span className="text-xs text-slate-400 block">{u.manufacturerName}</span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{unitStatusLabel(u.status)}</td>
                  <td className="px-4 py-2">
                    {u.isAvailable && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">wolny</span>}
                    {u.isReserved && (
                      <span className={`text-xs px-2 py-0.5 rounded ${u.reservationExpired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {u.reservationExpired ? 'rezerwacja wygasła' : 'zarezerwowany'}
                      </span>
                    )}
                    {u.isSold && (
                      <span className={`text-xs px-2 py-0.5 rounded ml-1 ${u.status === 'handed_over' ? 'bg-slate-100 text-slate-600' : 'bg-blue-100 text-blue-700'}`}>
                        {u.status === 'handed_over' ? 'wydany' : 'sprzedany'}
                      </span>
                    )}
                    {u.reservationConflict && (
                      <span className="text-xs text-red-700 ml-1" title="Rezerwacja dla innego klienta niż sprzedaż">
                        ⚠ konflikt
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {u.status === 'in_stock' ? (
                      <span className={age.overdue ? 'text-red-700 font-semibold' : 'text-slate-500'}>{age.label}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              )
            })}
            {units.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                  Brak egzemplarzy. Przyjmij pierwszy na plac.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {intake && <IntakeDialog onClose={() => setIntake(false)} onDone={() => { setIntake(false); reload() }} />}
    </div>
  )
}
